import { useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Copy, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { useQuery } from "@tanstack/react-query";

interface PixPaymentProps {
  onSuccess: () => void;
  planId?: string;
  couponCode?: string;
  displayAmount?: number;
  description?: string;
  transactionType?: string;
  prorationMeta?: any;
}

export function PixPayment({ onSuccess, planId = 'plus', couponCode = '', displayAmount, description, transactionType, prorationMeta }: PixPaymentProps) {
  const { tenantId } = useTenant();
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrCodeText, setQrCodeText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  

  // Buscar dados do plano do banco
  const { data: plan } = useQuery({
    queryKey: ["subscription-plan", planId],
    queryFn: async () => {
      if (!planId) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("id", planId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!planId,
  });

  const planPrice = plan?.price || 0;
  const finalAmount = typeof displayAmount === 'number' ? displayAmount : planPrice;
  const showDiscount = finalAmount !== planPrice;
  const planName = plan?.name || planId;

  const generatePixQRCode = async () => {
    if (!tenantId) {
      toast.error("Tenant não identificado");
      return;
    }

    console.log("[PixPayment] Iniciando geração do QR Code PIX", {
      tenantId,
      planId,
      amount: planPrice,
      timestamp: new Date().toISOString(),
    });

    setLoading(true);
    try {
      const payload = {
        tenant_id: tenantId,
        amount: typeof displayAmount === 'number' ? displayAmount : planPrice,
        description: description || `Assinatura ${planName} - Mensal`,
        plan_id: planId,
        coupon_code: couponCode || undefined,
        transaction_type: transactionType || 'payment',
        proration_meta: prorationMeta || undefined,
      };

      console.log("[PixPayment] Chamando função create-mercadopago-preference", payload);

      const { data, error } = await supabase.functions.invoke('create-mercadopago-preference', {
        body: {
          ...payload
        }
      });

      if (error) {
        console.error("[PixPayment] Erro retornado pelo Supabase function", error);
        throw error;
      }

      console.log("[PixPayment] Resposta da função", data);

      if (data?.activated_without_payment) {
        toast.success("Cupom aplicado, ativação realizada sem pagamento!");
        onSuccess();
        return;
      }
      if (data.qr_code && data.qr_code_base64) {
        setQrCode(`data:image/png;base64,${data.qr_code_base64}`);
        setQrCodeText(data.qr_code);
        toast.success("QR Code gerado com sucesso!");

        // Iniciar verificação do pagamento
        startPaymentCheck(data.payment_id);
      } else {
        console.warn("[PixPayment] Dados de QR Code ausentes na resposta", data);
        throw new Error("Erro ao gerar QR Code");
      }
    } catch (error: any) {
      console.error("[PixPayment] Exceção ao gerar PIX", error);
      toast.error("Erro ao gerar QR Code: " + (error.message || "Erro desconhecido"));
    } finally {
      console.log("[PixPayment] Finalizando geração do QR Code PIX", {
        tenantId,
        timestamp: new Date().toISOString(),
      });
      setLoading(false);
    }
  };

  const startPaymentCheck = (paymentId: string) => {
    setChecking(true);
    
    // Verificar pagamento a cada 5 segundos
    const interval = setInterval(async () => {
      try {
        const { data: transactions } = await supabase
          .from('subscription_transactions')
          .select('status')
          .eq('mercadopago_payment_id', paymentId)
          .eq('tenant_id', tenantId)
          .single();

        if (transactions?.status === 'approved') {
          clearInterval(interval);
          setChecking(false);
          toast.success("Pagamento aprovado! Plano Plus ativado!");
          onSuccess();
        }
      } catch (error) {
        // Continuar verificando
      }
    }, 5000);

    // Parar verificação após 10 minutos
    setTimeout(() => {
      clearInterval(interval);
      setChecking(false);
    }, 600000);
  };

  const copyToClipboard = () => {
    if (qrCodeText) {
      navigator.clipboard.writeText(qrCodeText);
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!qrCode) {
    return (
      <div className="space-y-4">
        
        <Alert>
          <AlertDescription>
            Ao clicar no botão abaixo, será gerado um QR Code PIX para pagamento único da primeira mensalidade.
            Após a confirmação do pagamento, sua assinatura será ativada automaticamente.
          </AlertDescription>
        </Alert>

        <Button
          onClick={generatePixQRCode}
          disabled={loading}
          className="w-full"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Gerando QR Code...
            </>
          ) : (
            "Gerar QR Code PIX"
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-4">
        <img
          src={qrCode}
          alt="QR Code PIX"
          className="w-64 h-64 border rounded-lg"
        />

        <div className="w-full space-y-2">
          <p className="text-sm text-muted-foreground text-center">
            Ou copie o código PIX abaixo:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={qrCodeText || ""}
              readOnly
              className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted"
            />
            <Button
              onClick={copyToClipboard}
              size="icon"
              variant="outline"
            >
              {copied ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {checking && (
          <Alert className="border-amber-500 bg-amber-500/10">
            <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
            <AlertDescription className="text-amber-900 dark:text-amber-100">
              Aguardando confirmação do pagamento...
            </AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-muted-foreground text-center">
          {showDiscount ? (
            <span>
              Valor: <span className="line-through">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(planPrice)}</span> → {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finalAmount)}
            </span>
          ) : (
            <span>
              Valor: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finalAmount)}
            </span>
          )} | Após o pagamento, sua assinatura será ativada automaticamente
        </p>
      </div>
    </div>
  );
}
